import { useEffect, useMemo, useRef, useState } from 'react';
import deviceInfoAxios from '../../../utils/axios/deviceInfo.axios';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';
import {
    ITipsFeedbackFilters,
    ITipsFeedbackItem,
    ITipsFeedbackResponse,
    TLeoFeedbackTab,
    TFeedbackDetailsSection,
    TLeoFeedbackType,
} from '../../../interfaces/tips-feedback/ITipsFeedback';
import { useGetTipsFeedbackMutation } from '../../../store/chatbox/chatboxApi';
import {
    ITipsFeedbackClientOption,
    useGetTipsFeedbackClientsQuery,
    useGetTipsFeedbackPropertiesQuery,
} from '../../../store/users/usersApi';
import {
    resolveBillyHasMore,
    resolveBillyPaginatedData,
} from '../../../utils/pagination/billyPagination';
import TipsFeedbackFilters from './components/TipsFeedbackFilters';
import TipsFeedbackDetailsModal from './components/TipsFeedbackDetailsModal';
import TipsFeedbackTable from './components/TipsFeedbackTable';
import TipsFeedbackAnalysis from './components/TipsFeedbackAnalysis';
import FeedbackDrawer from './components/FeedbackDrawer';
import FeedbackTypeSelect from './components/FeedbackTypeSelect';
import { feedbackErrorMessage, sanitizeFeedbackFilters } from './utils';

const DEFAULT_SORT = 'date,desc';

const getFeedbackType = (tab: TLeoFeedbackTab): TLeoFeedbackType =>
    tab === 'links' ? 'link_audit' : tab;

const TipsFeedback = () => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const requestNextPageRef = useRef(false);
    const [activeTab, setActiveTab] = useState<TLeoFeedbackTab>('tips');

    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 20,
    });

    const [filters, setFilters] = useState<ITipsFeedbackFilters>({});
    const [appliedFilters, setAppliedFilters] = useState<ITipsFeedbackFilters>({});
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [appliedSort, setAppliedSort] = useState(DEFAULT_SORT);
    const [refreshVersion, setRefreshVersion] = useState(0);
    const [listError, setListError] = useState('');
    const [exportError, setExportError] = useState('');
    const [downloadingExcel, setDownloadingExcel] = useState(false);
    const exportInProgress = useRef(false);

    const [rows, setRows] = useState<ITipsFeedbackItem[]>([]);
    const [pageData, setPageData] = useState<ITipsFeedbackResponse['page']>(undefined);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [detailsItem, setDetailsItem] = useState<ITipsFeedbackItem | undefined>();
    const [detailsSection, setDetailsSection] = useState<TFeedbackDetailsSection>('details');
    const [analysisOpen, setAnalysisOpen] = useState(false);
    const activeFeedbackType = getFeedbackType(activeTab);

    const [getTipsFeedback] = useGetTipsFeedbackMutation();
    const {
        data: clientsFromApi = [],
        isFetching: loadingClients,
    } = useGetTipsFeedbackClientsQuery();
    const {
        data: propertiesFromApi = [],
        isFetching: loadingProperties,
    } = useGetTipsFeedbackPropertiesQuery();

    const clientOptions = useMemo(
        () => (clientsFromApi || []).map((item): ITipsFeedbackClientOption => ({
            ...item,
            label: item.label || item.id,
        })),
        [clientsFromApi],
    );

    const propertyOptions = useMemo(() => {
        const selectedClientId = clientOptions.find((client) =>
            (client.companyName || client.name || client.label) === filters.client)?.id;
        const source = propertiesFromApi || [];
        if (!selectedClientId) return source;

        const hasClientScopedProperties = source.some((property) => Boolean(property?.clientId));
        if (!hasClientScopedProperties) return source;

        return source.filter((property) =>
            !property?.clientId || property.clientId === selectedClientId);
    }, [propertiesFromApi, filters.client, clientOptions]);

    useEffect(() => {
        let active = true;
        const fetchData = async () => {
            const isFirstPage = paginationState.pageNumber === 1;

            try {
                setListError('');
                if (isFirstPage) {
                    setLoading(true);
                } else {
                    setLoadingMore(true);
                }

                const result = await getTipsFeedback({
                    pageNumber: paginationState.pageNumber,
                    pageSize: paginationState.pageSize,
                    sort: appliedSort,
                    data: {
                        ...appliedFilters,
                        type: activeFeedbackType,
                    },
                }).unwrap();
                if (!active) return;

                const { elements, page } = resolveBillyPaginatedData<ITipsFeedbackItem>(result);

                setPageData(page as ITipsFeedbackResponse['page']);
                setRows((prevRows) => (isFirstPage ? elements : [...prevRows, ...elements]));
                setHasMore(resolveBillyHasMore(page, elements.length, paginationState.pageSize || 20));
            } catch (error) {
                if (!active) return;
                setListError(feedbackErrorMessage(error, 'Unable to load feedback. Please try again.'));
                if (isFirstPage) {
                    setRows([]);
                    setPageData(undefined);
                }
                setHasMore(false);
            } finally {
                if (active) {
                    requestNextPageRef.current = false;
                    setLoading(false);
                    setLoadingMore(false);
                }
            }
        };

        fetchData();
        return () => { active = false; };
    }, [
        getTipsFeedback,
        activeFeedbackType,
        paginationState.pageNumber,
        paginationState.pageSize,
        appliedFilters,
        appliedSort,
        refreshVersion,
    ]);

    const resetAndFetchFromFirstPage = (nextFilters: ITipsFeedbackFilters) => {
        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        setDetailsItem(undefined);
        setAnalysisOpen(false);
        setAppliedFilters(nextFilters);
        setListError('');
        setExportError('');
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
    };

    const onApplyFilters = () => {
        setAppliedSort(sort);
        resetAndFetchFromFirstPage(sanitizeFeedbackFilters(filters));
    };

    const onClearFilters = () => {
        const emptyFilters: ITipsFeedbackFilters = {};
        setFilters(emptyFilters);
        setSort(DEFAULT_SORT);
        setAppliedSort(DEFAULT_SORT);
        resetAndFetchFromFirstPage(emptyFilters);
    };

    const onTabChange = (tab: TLeoFeedbackTab) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        setDetailsItem(undefined);
        setAnalysisOpen(false);
        setExportError('');
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
    };

    const onLoadMore = () => {
        if (!hasMore || loadingMore || loading || requestNextPageRef.current) return;
        requestNextPageRef.current = true;
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: (prev.pageNumber || 1) + 1,
        }));
    };

    const onOpenDetails = (row: ITipsFeedbackItem, section: TFeedbackDetailsSection = 'details') => {
        setDetailsSection(section);
        setDetailsItem(row);
    };

    const onCloseDetails = () => {
        setDetailsItem(undefined);
        setAnalysisOpen(false);
    };

    const onFeedbackSaved = () => {
        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
        setPaginationState((previous) => ({ ...previous, pageNumber: 1 }));
        setRefreshVersion((version) => version + 1);
    };

    const onFiltersChange = (nextFilters: ITipsFeedbackFilters) => {
        const nextClient = String(nextFilters?.client || '').trim();
        const currentClient = String(filters?.client || '').trim();

        if (nextClient !== currentClient) {
            setFilters({
                ...nextFilters,
                property: undefined,
            });
            return;
        }

        setFilters(nextFilters);
    };

    const onDownloadExcel = async () => {
        if (exportInProgress.current) return;
        exportInProgress.current = true;
        setDownloadingExcel(true);
        setExportError('');
        try {
            const result = await deviceInfoAxios.put(
                `/billy/tips-feedback/export?sort=${encodeURIComponent(appliedSort)}`,
                { ...appliedFilters, type: activeFeedbackType },
                { responseType: 'blob' },
            );
            const url = window.URL.createObjectURL(result.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${activeTab}-feedback-${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setExportError(feedbackErrorMessage(error, 'Unable to export feedback. Please try again.'));
        } finally {
            exportInProgress.current = false;
            setDownloadingExcel(false);
        }
    };

    return (
        <div className='w-full'>
            <div className='flex flex-wrap items-center justify-between gap-4'>
                <div>
                    <h1 className='text-xl font-bold text-[#101010] dark:text-primary-darkMode md:text-2xl'>Leo {activeTab === 'challenge' ? 'Challenge' : activeTab === 'links' ? 'Link' : 'Tips'} Feedback</h1>
                    <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>Review feedback from Leo AI Visibility Agent</p>
                </div>
                <FeedbackTypeSelect value={activeTab} onChange={onTabChange} />
            </div>

            <TipsFeedbackFilters
                totalElements={pageData?.totalElements || 0}
                filters={filters}
                loading={loading}
                loadingFilterOptions={loadingClients || loadingProperties}
                clientOptions={clientOptions}
                propertyOptions={propertyOptions}
                downloadingExcel={downloadingExcel}
                sort={sort}
                onSortChange={setSort}
                onChange={onFiltersChange}
                onApply={onApplyFilters}
                onClear={onClearFilters}
                onDownloadExcel={onDownloadExcel}
                onOpenAnalysis={() => setAnalysisOpen(true)}
            />

            {analysisOpen && <FeedbackDrawer open onClose={() => setAnalysisOpen(false)} title='Meta Analysis'>
                <TipsFeedbackAnalysis
                    key={`${activeFeedbackType}:${JSON.stringify(appliedFilters)}:${refreshVersion}`}
                    filters={{ ...appliedFilters, type: activeFeedbackType }}
                />
            </FeedbackDrawer>}

            {exportError && <p role='alert' className='my-3 text-sm text-red-600 dark:text-red-400'>{exportError}</p>}
            {listError && (
                <div role='alert' className='my-3 text-sm text-red-600 dark:text-red-400'>
                    {listError}
                    <button type='button' onClick={() => setRefreshVersion((version) => version + 1)} disabled={loading || loadingMore} className='ml-2 underline'>Retry</button>
                </div>
            )}

            <TipsFeedbackTable
                rows={rows}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={listError ? undefined : hasMore}
                scrollContainerRef={scrollContainerRef}
                onLoadMore={onLoadMore}
                onOpenDetails={onOpenDetails}
            />

            <TipsFeedbackDetailsModal
                activeTab={activeTab}
                initialSection={detailsSection}
                open={!!detailsItem}
                onClose={onCloseDetails}
                item={detailsItem}
                onSaved={onFeedbackSaved}
            />
        </div>
    );
};

export default TipsFeedback;
